/**
 * Value formatting — plan §3.8's copy rules, in one place so a number never
 * gets formatted two different ways on two different screens.
 *
 * "Numbers with thousands separators; money `$1,234`; hours `7.5 h`; dates
 * `Thu Sep 17` in lists, `Sep 17, 2026` in documents."
 */
import type { ListFieldDef } from './use-list.js'

export const EM_DASH = '—'

const numberFormat = new Intl.NumberFormat('en-US')

/** Money is shown whole in lists: §3.8 writes `$1,234`, not `$1,234.00`. */
const moneyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const listDateFormat = new Intl.DateTimeFormat('en-US', {
  weekday: 'short', month: 'short', day: 'numeric',
})

/** The same, for a date with no time in it. */
const listDayFormat = new Intl.DateTimeFormat('en-US', {
  weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
})

/**
 * `Sep 28, 2:03 pm` — a list cell for a column that is an *instant*.
 *
 * Not the same as `listDateFormat`, and the difference matters: a feed of
 * sign-ins sorted newest-first where thirty consecutive rows all read
 * "Mon, Sep 28" is a column carrying no information. The weekday goes, because
 * the time is what tells the rows apart and there is not room for both.
 */
const listTimeFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
})

const documentDateFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', year: 'numeric',
})

/** The same, for a date with no time in it. */
const documentDayFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
})

export const formatNumber = (value: number): string => numberFormat.format(value)

/*
 * Two functions, because there are two units and `formatMoney` did not say
 * which it wanted. Money is held in cents (addendum §A.1), but a `numeric`
 * column arrives as dollars, and every call site had to remember which it had
 * and divide by 100 — or not. One that forgot printed $1,894,000 for an
 * $18,940 quote, and nothing but a person reading the screen could catch it.
 *
 * Naming the unit makes the mistake unwriteable, and makes every conversion
 * greppable.
 */

/** For money held in cents, which is most of it. */
export const formatCents = (cents: number): string => moneyFormat.format(cents / 100)

/** For a `numeric` column, which Postgres hands back in dollars as a string. */
export const formatDollars = (dollars: number): string => moneyFormat.format(dollars)

/**
 * A unit rate keeps its cents: $24.10 per sq ft is not $24 per sq ft, and on a
 * quote the difference is $64 over a patio. §3.8's `$1,234` is about totals in
 * a list, where the cents are noise; a rate is the number being multiplied.
 */
const rateFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const formatRate = (value: number): string => rateFormat.format(value)

/**
 * Money the server sent as a decimal string (*"1140.00"* → *$1,140.00*,
 * *"8888"* → *$8,888*) — the visit page's `Money`, which is never a JS number
 * on its way here, so it is grouped as text rather than parsed.
 */
export function formatMoneyText(value: string): string {
  const negative = value.startsWith('-') || value.startsWith('\u2212')
  const [whole = '0', cents] = (negative ? value.slice(1) : value).split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  // The boards draw a negative with the minus sign (−$58.40), not a hyphen.
  return `${negative ? '\u2212' : ''}$${grouped}${cents === undefined ? '' : `.${cents}`}`
}

/**
 * A unit cost, which may need the four places a cost per square foot does —
 * `$0.9356`, but `$230.00` (Board 1's Readout, Board 3's Cost column).
 */
const unitCostFormat = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4,
})
export const formatUnitCost = (value: number): string => unitCostFormat.format(value)

/**
 * Money on a document somebody signs — **cents only when there are cents**.
 *
 * §3.8's `$1,234` is the rule for a list, where the cents are noise. A
 * proposal is the other case: the customer is agreeing to a number, and
 * `$3,527` beside a signature line when the invoice will say `$3,527.72` is a
 * figure they will query. Rounding *up* would be worse again.
 *
 * But `$864.00` for a price that is 864 dollars is a document padded with
 * noughts, and every figure on `design/mockups/43-customer-proposal` is
 * written this way: `$1,260`, `$69.72`, `$3,527.72`. One rule produces all
 * three.
 */
export const formatDollarsExact = (dollars: number): string =>
  (Math.round(dollars * 100) % 100 === 0 ? moneyFormat : rateFormat).format(dollars)

export const formatHours = (value: number): string => `${value.toFixed(1)} h`

/**
 * A calendar date, whatever shape it arrived in.
 *
 * A Postgres `date` reaches the client as either `2026-09-26` or
 * `2026-09-26T00:00:00.000Z`, depending on the driver and the query — and
 * appending `T00:00:00Z` to the second one produces an invalid date, which is
 * how "By Invalid Date" got onto the scheduling queue.
 *
 * Parsed as UTC either way, because a calendar date has no timezone: a due
 * date of the 26th is the 26th in Harrisburg and in Perth.
 */
export function parseDateOnly(value: string | Date | null | undefined): Date | null {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  const date = new Date(`${value.slice(0, 10)}T00:00:00Z`)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * **A string with a date in it and no time of day is a day**, in either shape.
 *
 * `withTenant`'s type parser hands a Postgres `date` back as `2026-12-02`
 * now, which is what the formatters below want. This stays as the belt: a
 * payload built by hand, a `timestamptz` column that is really a day, or any
 * reader that has not been through that parser can still arrive carrying
 * `2026-12-02T00:00:00.000Z` — and read as an instant, that is the 1st
 * everywhere west of Greenwich.
 *
 * The cost is a genuine timestamp landing on exactly midnight UTC, which then
 * prints as a day rather than as "12:00 am". That is one row in a blue moon
 * against every date column in every list being a day early for every reader
 * in the Americas.
 */
const DAY_ONLY = /^\d{4}-\d{2}-\d{2}(T00:00:00(\.000)?Z)?$/

export const isDayOnly = (value: unknown): value is string =>
  typeof value === 'string' && DAY_ONLY.test(value)

const dateOnlyFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', timeZone: 'UTC',
})

/** `Sep 26` — a due date, a window edge, a day column. */
export function formatDateOnly(value: string | Date | null | undefined): string {
  const date = parseDateOnly(value)
  return date ? dateOnlyFormat.format(date) : EM_DASH
}

/**
 * `Thu Sep 17` — what a list cell shows. A plain `YYYY-MM-DD` is a day and is
 * read in UTC, for the reason given on `formatDocumentDate`: otherwise every
 * scheduled date in every list reads a day early west of Greenwich.
 */
export function formatListDate(value: string | Date): string {
  if (isDayOnly(value)) {
    const day = parseDateOnly(value)
    return day ? listDayFormat.format(day) : EM_DASH
  }
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return EM_DASH
  return listDateFormat.format(date)
}

/**
 * `Sep 28, 2:03 pm` — a list cell whose column is a moment rather than a day.
 *
 * A bare `YYYY-MM-DD` still goes through the day path: the server says a
 * column is a `datetime`, but a value with no time in it has none to print,
 * and "Sep 28, 12:00 am" would be a precision nobody recorded.
 */
export function formatListDateTime(value: string | Date): string {
  if (isDayOnly(value)) return formatListDate(value)
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return EM_DASH
  return listTimeFormat.format(date).replace(/\bAM\b/, 'am').replace(/\bPM\b/, 'pm')
}

/**
 * `Sep 17, 2026` — what a document or a profile header shows.
 *
 * A day is read as a day, not an instant. `new Date('2022-04-12')` is midnight
 * UTC, and formatting that in a zone behind UTC gives the 11th — so a truck
 * bought on the 12th was purchased on the 11th all down the east coast.
 * Anything carrying a real time of day is formatted where the reader is,
 * which is what a timestamp means.
 */
export function formatDocumentDate(value: string | Date): string {
  /* The same guard the list formatters use: either shape of a day. */
  if (isDayOnly(value)) {
    const day = parseDateOnly(value)
    return day ? documentDayFormat.format(day) : EM_DASH
  }
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return EM_DASH
  return documentDateFormat.format(date)
}

/** "2d ago", "6d ago", "just now" — the profile header's Last contact. */
export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return EM_DASH
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return EM_DASH

  const seconds = Math.round((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  return formatDocumentDate(date)
}

/**
 * One list cell, formatted by the column's declared kind.
 *
 * The kind comes from the server's field catalogue rather than from sniffing
 * the value, so an empty column still formats as the right kind of empty.
 */
export function formatCell(
  value: unknown,
  kind: ListFieldDef['kind'],
  /**
   * An enum's own words, from the area's catalogue.
   *
   * Without them a Status column printed `card_needed` and a Two-step column
   * printed `pin` — the raw values, while the filter beside the table offered
   * "Card needed" and "PIN only" for the same column. The server already
   * sends the labels; the table just was not reading them, so every screen
   * that wanted words wrote a `cell` renderer of its own.
   */
  options?: ListFieldDef['options'],
): string {
  if (value === null || value === undefined || value === '') return EM_DASH

  if (kind === 'enum' && options) {
    const match = options.find((one) => one.value === String(value))
    if (match) return match.label
  }

  switch (kind) {
    case 'money':
      return formatDollars(Number(value))
    case 'number':
      return Number.isFinite(Number(value)) ? formatNumber(Number(value)) : EM_DASH
    case 'date':
      return formatListDate(String(value))
    case 'datetime':
      return formatListDateTime(String(value))
    case 'boolean':
      return value ? 'Yes' : 'No'
    default:
      return String(value)
  }
}

/**
 * A number with the digits a card has room for — "$412k", "$2.41M", "146".
 *
 * Stat cards sit four across and the number is 28px, so `$412,371.50` either
 * wraps or shrinks until it is not the thing you notice on the card. Rounding
 * is the point rather than a compromise: nobody reads a dashboard to the
 * cent, and the exact figure is one click away in the list the card filters
 * to.
 */
export function formatCompact(value: number, kind: 'money' | 'number' = 'number'): string {
  const sign = value < 0 ? '-' : ''
  const size = Math.abs(value)
  const prefix = kind === 'money' ? '$' : ''

  if (size >= 1_000_000) return `${sign}${prefix}${trim(size / 1_000_000)}M`
  if (size >= 10_000) return `${sign}${prefix}${Math.round(size / 1000)}k`
  // Below ten thousand there is room for the real number, and "9k" for 9,412
  // loses more than it saves.
  if (kind === 'money') return moneyFormat.format(value)
  return numberFormat.format(value)
}

/** Two significant decimals, without a trailing `.00`. */
const trim = (value: number): string =>
  value.toFixed(2).replace(/\.?0+$/, '')

/**
 * `Old Orchard Business Park · 9 Ridge St, Harrisburg`, or just the address.
 *
 * A property's name is often its address — imports and the portal both name a
 * site after where it is — and a meta line that always appends the address
 * then reads "2025 Cape Horn Rd, Camp Hill · 2025 Cape Horn Rd, Camp Hill",
 * spending its width saying the same thing twice. So the address is added
 * only when the name is not already it.
 */
export function placeLine(
  name: string, addressLine1: string, city: string,
): string {
  const address = [addressLine1, city].filter(Boolean).join(', ')
  if (!address) return name
  const normalise = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '')
  return normalise(name).startsWith(normalise(address)) ? name : `${name} · ${address}`
}
